import { Box, Divider, Form, Grid, Text } from '@approvalmax/ui/src/components';
import { constants, selectors } from 'modules/common';
import { backend } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { memo, useCallback } from 'react';

import { AttachmentsSection, RequesterInstructionSection } from '../../../components';
import { getContext } from '../../../selectors/pageSelectors';
import { getRequestEditMode } from '../../../selectors/requestSelectors';
import { cleanUpRequestNote, getXeroContext } from '../../../selectors/xero';
import ActionsBlock from '../../form/ActionsBlock/ActionsBlock';
import { AddressPart } from './components/AddressPart/AddressPart';
import { ContactInformationPart } from './components/ContactInformationPart/ContactInformationPart';
import { FinancialDetailsPart } from './components/FinancialDetailsPart/FinancialDetailsPart';
import { SettingsPart } from './components/SettingsPart/SettingsPart';
import { TaxPart } from './components/TaxPart/TaxPart';
import { mapFormToTransfer } from './XeroContactRequestForm.helpers';
import { useRequestForm, useSaveAndSubmitContact } from './XeroContactRequestForm.hooks';
import { messages } from './XeroContactRequestForm.messages';
import { XeroContactRequestFormProps } from './XeroContactRequestForm.types';

export const XeroContactRequestForm = memo<XeroContactRequestFormProps>((props) => {
    const { request } = props;

    const requestContext = useSelector(getContext);
    const xeroContext = useSelector(getXeroContext);
    const editMode = useSelector((state) => getRequestEditMode(state, request));

    const company = useSelector((state) => selectors.company.findCompanyById(state, request.companyId));

    const isUKCompany = company?.integration?.countryCode === backend.OrganisationVersion.UK;

    const { form, handleFormValidation } = useRequestForm(request.details, isUKCompany);

    const getRequestTransfer = useCallback((): backend.Transfer<backend.transfers.RequestEditTransfer> => {
        const values = form.getValues();

        const transfer: backend.Transfer<backend.transfers.RequestEditTransfer> = {
            requestId: request.id,
            currency: request.currency,
            attachments: request.attachments.map((attachment) => attachment.id),
            requestNote: cleanUpRequestNote(request.requestNote.value),
            templateVersion: requestContext.templateVersion,
            companyId: request.companyId,
        };

        return {
            ...transfer,
            xeroContactDetails: mapFormToTransfer(values, xeroContext.trackingCategories),
        };
    }, [form, request, requestContext.templateVersion, xeroContext.trackingCategories]);

    const { saveAndSubmitContact, isSaving } = useSaveAndSubmitContact({
        requestId: request.id,
        companyId: request.companyId,
        getRequestTransfer,
        validateForm: handleFormValidation,
    });

    return (
        <Form form={form}>
            <Box spacing='0 0 28 0'>
                <Grid gridTemplateColumns='auto max-content' alignItems='center'>
                    <Text color='midnight80' font='headline' fontSize='xsmall' fontWeight='medium'>
                        {messages.title}
                    </Text>

                    <ActionsBlock
                        customRequestValidationFn={handleFormValidation}
                        customGetRequestTransfer={getRequestTransfer}
                        customSaveAndSubmitRequest={saveAndSubmitContact}
                        isSaving={isSaving}
                    />
                </Grid>
            </Box>

            <Divider />

            <RequesterInstructionSection instructionText={xeroContext.requesterInstruction} />

            <Grid rowGap={32}>
                <ContactInformationPart />

                <AddressPart name='billingAddress' title={messages.billingAddress} qa='billing-address' />

                <AddressPart
                    name='deliveryAddress'
                    title={messages.deliveryAddress}
                    isSameCheckboxVisible
                    sameCheckboxFormName='deliveryAddress.sameAsBillingAddress'
                    sameCheckboxTitle={messages.sameAsBillingAddress}
                    qa='delivery-address'
                />

                <Grid gridTemplateColumns='1fr 1fr' columnGap={16}>
                    <SettingsPart
                        name='salesSettings'
                        title={messages.salesSettings}
                        accounts={xeroContext.accounts}
                        trackingCategories={xeroContext.trackingCategories}
                        qa='sales-settings'
                    />

                    <SettingsPart
                        name='purchaseSettings'
                        title={messages.purchaseSettings}
                        accounts={xeroContext.accounts}
                        trackingCategories={xeroContext.trackingCategories}
                        qa='purchase-settings'
                    />
                </Grid>

                <TaxPart taxCodes={xeroContext.taxCodes} />

                <FinancialDetailsPart isUKCompany={isUKCompany} currencies={xeroContext.currencies} />

                <Grid gridTemplateColumns='50%' gap={16}>
                    <AttachmentsSection
                        request={request}
                        editMode={editMode}
                        extensions={constants.xeroConstants.xeroAllowedFileExtensions}
                        maxFilesInList={constants.xeroConstants.maxFileCount}
                    />
                </Grid>
            </Grid>
        </Form>
    );
});

XeroContactRequestForm.displayName = 'XeroContactRequestForm';
